import { useEffect, useState } from 'react';
import { MousePointerClick, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// One-time educational hint surfaced on the first table visit.
// Dismissed by clicking, by Escape, or after the user opens any row menu.
// Persisted via localStorage so we don't re-prompt across sessions.
// Lives at the top of the table view, restrained — no toast, no overlay.

const STORAGE_KEY = 'rentallens.hints.rightClickRow';

export function RightClickHint() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.localStorage.getItem(STORAGE_KEY) !== 'seen';
  });

  useEffect(() => {
    if (!visible) return;
    function dismiss() {
      window.localStorage.setItem(STORAGE_KEY, 'seen');
      setVisible(false);
    }
    function onContextMenu() {
      // Once the user discovers right-click on a row, retire the hint.
      const handler = setTimeout(dismiss, 0);
      return () => clearTimeout(handler);
    }
    document.addEventListener('contextmenu', onContextMenu, { once: true });
    return () => document.removeEventListener('contextmenu', onContextMenu);
  }, [visible]);

  if (!visible) return null;

  function close() {
    window.localStorage.setItem(STORAGE_KEY, 'seen');
    setVisible(false);
  }

  return (
    <div
      role="note"
      data-testid="right-click-hint"
      className="flex h-8 shrink-0 items-center gap-2 border-b border-rule bg-accent/8 px-3 text-2xs text-accent"
    >
      <MousePointerClick size={12} />
      <span>
        {t('rightClickHint.body')}
        <kbd className="num mx-1 rounded-xs border border-accent/30 px-1">⋯</kbd>
        {t('rightClickHint.bodyTail')}
      </span>
      <button
        type="button"
        aria-label={t('rightClickHint.dismiss')}
        onClick={close}
        className="ml-auto grid h-5 w-5 place-items-center rounded-xs hover:bg-accent/15"
      >
        <X size={12} />
      </button>
    </div>
  );
}
